<template>
  <div class="kpi-row-editor">
    <div class="editor-header">
      <div class="widget-type">
        <img
          :src="moduleConfigs.icon"
          alt="" />
        <div>{{ isPrimary ? 'Primary Row' : 'Secondary Row' }}</div>
      </div>
      <Tabs
        v-model="inputSectionTab"
        :tabs="tabs"
        :is-objects="true"
        @input="scrollToSection" />
    </div>

    <div
      class="editor"
      @scroll="throttleScroll">
      <div
        id="display-label"
        class="editor-section">
        <h2>Display label</h2>
        <div class="body">
          <b-field
            label="Display Label"
            class="input-field">
            <MeasureLabelInput
              v-model="inputs.name"
              :placeholder="isPrimary ? 'Defaults to the measure name' : 'e.g. LY'" />
          </b-field>
          <p class="section-help">
            This row is rendered once per KPI measure. Leave the label empty to
            use the measure's own name.
          </p>
        </div>
      </div>

      <div
        id="measure"
        class="editor-section">
        <h2>Measure</h2>
        <div class="body">
          <MeasureConfigEditor
            v-model="inputs.measure_config"
            :errors="childErrors('measure_config', errors)"
            :context="{
              reportDimensions: [],
              defaultMeasureOption: inheritMeasureOption,
              tableDefaultTimeRange: context.moduleDefaultTimeRange,
              timeRangeDefaultLabel: 'Use the KPI measure default',
              timeRangeDefaultName: context.timeRangeDefaultName,
            }" />
          <p class="section-help">
            Anything left unset here comes from the KPI's own measure. A
            benchmark row is usually just a time offset with the measure left
            inherited.
          </p>

          <MeasureMappingSelect
            v-model="inputs.measure_config"
            :kpi-measures="context.kpiMeasures || []"
            help="Reads this row through a mapping of the KPI's own measure —
              each card resolves its own. Use this instead of naming a measure
              above, which would mean the same one on every card." />
        </div>
      </div>

      <div
        id="benchmark"
        class="editor-section">
        <h2>Benchmark</h2>
        <div class="body">
          <BenchmarkConfigEditor
            v-model="inputs.comparison_config"
            :errors="childErrors('comparison_config', errors)"
            :hide-direction="hasMeasureDirection"
            :extra-difference-type-options="extraDifferenceTypeOptions"
            :context="{
              parentMeasure: null,
              reportDimensions: [],
              defaultMeasureOption: inheritMeasureOption,
              tableDefaultTimeRange: context.moduleDefaultTimeRange,
              timeRangeDefaultLabel: 'Use this row default',
              timeRangeDefaultName: context.timeRangeDefaultName,
            }">
            <template #direction>
              <b-field class="input-field">
                <b-switch
                  v-model="inputs.invert_direction"
                  type="is-info">
                  Invert good/bad for this row
                </b-switch>
              </b-field>
              <p class="section-help">
                {{ directionHelp }}
              </p>
            </template>
          </BenchmarkConfigEditor>

          <MeasureMappingSelect
            v-model="benchmarkMeasureConfig"
            label="Benchmark measure mapping"
            none-label="None — offset the row's own measure"
            :kpi-measures="context.kpiMeasures || []"
            help="How a benchmark names a different measure per card — a
              budget or forecast. Each KPI resolves its own through the
              mapping, so one row serves every card." />
          <p
            v-if="!hasMeasureDirection"
            class="section-help">
            No direction is set on the KPI measures, so each row still carries
            its own. Set one per measure to colour every row from one place.
          </p>
        </div>
      </div>

      <div
        id="format"
        class="editor-section">
        <h2>Format</h2>
        <div class="body">
          <DataFormatEditor
            v-model="inputs.data_format"
            :errors="childErrors('data_format', errors)"
            :measure="inputs.measure_config?.measure"
            :type-override="formatTypeOverride" />
          <p class="section-help">
            {{ derivesFormatFromMeasure
              ? 'Leave unset and each measure picks its own — a percentage where'
                + ' it reads as a percentage change, its own format where it reads'
                + ' as a difference.'
              : "Leave unset to use the KPI measure's format." }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import Tabs from '@/components/pdf_report/Tabs.vue'
  import EditorSectionsMixin from '@/components/pdf_report/EditorSectionsMixin'
  import MeasureLabelInput from '@/components/pdf_report/MeasureLabelInput.vue'
  import MeasureConfigEditor from '@/components/reports/config/modules/common/MeasureConfigEditor'
  import BenchmarkConfigEditor from '@/components/reports/config/modules/common/BenchmarkConfigEditor'
  import DataFormatEditor from '@/components/reports/config/modules/common/DataFormatEditor'
  import MeasureMappingSelect from '@/components/pdf_report/kpi_module/MeasureMappingSelect.vue'
  import { childErrors as _childErrors } from '@/components/reports/config/common/validation'

  export default {
    name: 'KpiRow',
    components: {
      Tabs,
      MeasureLabelInput,
      MeasureConfigEditor,
      BenchmarkConfigEditor,
      DataFormatEditor,
      MeasureMappingSelect,
    },
    mixins: [EditorSectionsMixin,],
    props: {
      value: {
        type: Object,
        default: () => {},
      },
      errors: {
        type: Array,
        default: () => [],
      },
      context: {
        type: Object,
        default: () => {},
      },
      isPrimary: {
        type: Boolean,
        default: false,
      },
    },
    data () {
      return {
        moduleConfigs: {
          name: 'KPI Row',
          type: 'KpiRow',
          icon: require('@/assets/icons/kpi-row.svg'),
        },
        inputSectionTab: 'display-label',

        directionLabels: {
          higher_is_better: 'higher is better',
          lower_is_better: 'lower is better',
          neutral: 'neutral, never coloured',
        },
      }
    },
    computed: {
      inputs: {
        get () {
          return this.value
        },
        set (value) {
          this.$emit('input', value)
        },
      },

      // The benchmark's own measure_config, which the mapping picker writes
      // into. Both it and comparison_config are nullable, so choosing a
      // mapping on a row with no benchmark yet has to build the pair.
      benchmarkMeasureConfig: {
        get () {
          return this.inputs.comparison_config?.measure_config || null
        },
        set (measureConfig) {
          this.$set(this.inputs, 'comparison_config', {
            ...(this.inputs.comparison_config || { difference_type: 'none', }),
            measure_config: measureConfig,
          })
        },
      },
      // Direction only moves to the measure once at least one measure sets
      // it; until then the row keeps the control it has always had, so a
      // half-configured module never silently loses its colouring.
      hasMeasureDirection () {
        return (this.context.kpiMeasures || []).some(
          (kpiMeasure) => !!kpiMeasure.good_direction
        )
      },
      directionHelp () {
        const described = (this.context.kpiMeasures || [])
          .filter((kpiMeasure) => !!kpiMeasure.good_direction)
          .map((kpiMeasure) => `${kpiMeasure.name || 'Untitled'} — ${
            this.directionLabels[kpiMeasure.good_direction]
          }`)
        if (!described.length) return ''
        const base = this.inputs.invert_direction
          ? 'This row is coloured against each measure, inverted'
          : 'This row is coloured in each measure\'s own direction'
        return `${base}: ${described.join(', ')}.`
      },
      extraDifferenceTypeOptions () {
        return [
          {
            key: 'measure',
            label: 'Difference, as each measure defines it',
          },
        ]
      },
      inheritMeasureOption () {
        // Selecting this clears the measure, which is what makes the row a
        // template: it takes whichever measure the card it lands on carries.
        return { name: 'Use the KPI measure', }
      },
      formatTypeOverride () {
        if (this.inputs.measure_config?.is_pct_total) return 'percentage'
        if (this.inputs.comparison_config?.difference_type === 'var') return 'percentage'
        return null
      },
      // With 'measure' the row has no single format — each measure resolves
      // its own, a percentage for var and the measure's format for diff.
      derivesFormatFromMeasure () {
        return this.inputs.comparison_config?.difference_type === 'measure'
      },
      tabs () {
        return [
          {
            label: 'Display label',
            id: 'display-label',
            hasErrors: false,
          },
          {
            label: 'Measure',
            id: 'measure',
            hasErrors: _childErrors('measure_config', this.errors).length > 0,
          },
          {
            label: 'Benchmark',
            id: 'benchmark',
            hasErrors: _childErrors('comparison_config', this.errors).length > 0,
          },
          {
            label: 'Format',
            id: 'format',
            hasErrors: _childErrors('data_format', this.errors).length > 0,
          },
        ]
      },
    },
    methods: {
      childErrors: _childErrors,
    },
  }
</script>

<style lang="scss" scoped>
@import '@/components/pdf_report/kpi_module/kpi-editor-panel.scss';
</style>
